import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, FormControl, Select, MenuItem, InputLabel,
} from '@mui/material';
import HealthFormStep from '../components/TravelerScreening/steps/HealthFormStep';
import PassDisplayStep from '../components/TravelerScreening/steps/PassDisplayStep';
import { buildScreeningPayload, validatePublicScreenForm } from '../components/TravelerScreening/buildScreeningPayload';
import { publicSelfScreenAPI } from '../services/api';
import {
  PUBLIC_LANG_OPTIONS,
  PublicLang,
  tPublic,
  isRtl,
} from '../components/TravelerScreening/publicScreenI18n';

const FOREST = '#0b5c45';
const FOREST_DEEP = '#063d2e';
const LEAF = '#1a8f6a';
const PAGE = '#e8f0eb';
const LINE = '#d5e3dc';
const SCREEN_TITLE = 'UHAI';
const SCREEN_DESC = 'Ministry of Health - Kenya';
const COL = {
  width: '100%',
  maxWidth: 420,
  mx: 'auto',
  px: 2,
  boxSizing: 'border-box' as const,
};

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Public traveler self-screen — phone-first, no login.
 * Logo matches LoginPage; pass is token-only (no internal ids).
 */
const PublicSelfScreenPage: React.FC = () => {
  const [lang, setLang] = useState<PublicLang>('en');
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedScreening, setSavedScreening] = useState<Record<string, unknown> | null>(null);

  const L = useMemo(() => (key: string) => tPublic(lang, key), [lang]);
  const rtl = isRtl(lang);

  // Share / tab title — replace default "Converse" branding on this route
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    document.title = SCREEN_TITLE;
    setMeta('name', 'description', SCREEN_DESC);
    setMeta('property', 'og:title', SCREEN_TITLE);
    setMeta('property', 'og:description', SCREEN_DESC);
    setMeta('property', 'og:type', 'website');
    setMeta('name', 'twitter:title', SCREEN_TITLE);
    setMeta('name', 'twitter:description', SCREEN_DESC);
    return () => {
      document.title = prevTitle;
      if (prevDesc) setMeta('name', 'description', prevDesc);
    };
  }, []);

  const handleFormChange = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const missingKey = validatePublicScreenForm(form);
    if (missingKey) {
      setError(L(missingKey));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const cleaned = buildScreeningPayload(form);
      const result = await publicSelfScreenAPI.createSelfScreen(cleaned);
      setSavedScreening(result);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : L('err_save'));
    } finally {
      setSaving(false);
    }
  };

  const handleNew = () => {
    setForm({});
    setError(null);
    setSavedScreening(null);
  };

  return (
    <Box
      dir={rtl ? 'rtl' : 'ltr'}
      sx={{
        minHeight: '100vh',
        bgcolor: PAGE,
        background: `linear-gradient(180deg, #dceee6 0%, ${PAGE} 35%, #f2f7f4 100%)`,
        pb: savedScreening ? 4 : 'calc(88px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', height: 5 }}>
        <Box sx={{ bgcolor: '#000' }} />
        <Box sx={{ bgcolor: '#bb0000' }} />
        <Box sx={{ bgcolor: '#fff' }} />
        <Box sx={{ bgcolor: '#006600' }} />
      </Box>

      <Box sx={{ ...COL, pt: 1.5 }}>
        <Box
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: '14px',
            overflow: 'hidden',
            border: `1px solid ${LINE}`,
            mb: 1.5,
            bgcolor: '#fff',
            px: 2,
            pt: 2.5,
            pb: 2,
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <img
              src="/logo/ebola_logo.png"
              alt="Ebola"
              style={{
                height: '70px',
                width: 'auto',
                maxWidth: '45%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <img
              src="/logo/NPHI.jpeg"
              alt="KNPHI"
              style={{
                height: '70px',
                width: 'auto',
                maxWidth: '45%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2, textAlign: 'left' }}>
            <InputLabel id="public-lang-label">{L('language')}</InputLabel>
            <Select
              labelId="public-lang-label"
              label={L('language')}
              value={lang}
              onChange={(e) => setLang(e.target.value as PublicLang)}
            >
              {PUBLIC_LANG_OPTIONS.map((o) => (
                <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: FOREST,
            }}
          >
            {L('brand_eyebrow')}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontWeight: 700,
              fontFamily: '"Fraunces", "Georgia", serif',
              fontSize: '1.3rem',
              lineHeight: 1.2,
              color: FOREST_DEEP,
            }}
          >
            {L('brand_title')}
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13, color: '#3d5a50', lineHeight: 1.35 }}>
            {L('brand_sub')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, width: '100%' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!savedScreening ? (
          <HealthFormStep
            form={form}
            onChange={handleFormChange}
            subTab={0}
            subTabLabels={[]}
            variant="public"
            lang={lang}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              p: 2,
              borderRadius: '14px',
              border: `1px solid ${LINE}`,
              bgcolor: '#fff',
            }}
          >
            {(() => {
              const passType = (savedScreening.pass_record as { pass_type?: string } | undefined)?.pass_type;
              const highRisk = passType && passType !== 'cleared';
              return (
                <Alert
                  severity={highRisk ? 'warning' : 'success'}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: highRisk ? '#fff4e5' : '#e8f8ef',
                    border: highRisk ? '1px solid #ffcc80' : '1px solid #a7e0bf',
                    color: highRisk ? '#8a4b08' : '#146c43',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {highRisk ? L('success_high_risk') : L('success_cleared')}
                </Alert>
              );
            })()}
            <Box sx={{ width: '100%', '& > *': { maxWidth: '100% !important', mx: '0 !important' } }}>
              <PassDisplayStep screening={savedScreening} publicSafe lang={lang} />
            </Box>
            <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
              <Button onClick={handleNew} sx={{ color: FOREST, fontWeight: 700, textTransform: 'none' }}>
                {L('another')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {!savedScreening && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            pt: 1.5,
            pb: 'calc(10px + env(safe-area-inset-bottom, 0px))',
            background: 'linear-gradient(180deg, transparent, #e8f0eb 30%)',
          }}
        >
          <Box sx={COL}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{
                minHeight: 52,
                borderRadius: '12px',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 16,
                background: `linear-gradient(135deg, ${FOREST} 0%, ${LEAF} 100%)`,
                boxShadow: '0 10px 24px rgba(11,92,69,.3)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${FOREST_DEEP} 0%, ${FOREST} 100%)`,
                },
              }}
            >
              {saving ? L('submitting') : L('submit')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PublicSelfScreenPage;
